import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, FieldArray } from 'formik';
import {
    getDeploymentById, updateDeployment, addEnvironment, updateEnvironment, deleteEnvironment,
    Deployment, DeploymentEnvironment,
} from '../api/environmentApi';
import {
    ArrowLeftIcon, PlusIcon, TrashIcon, PencilSquareIcon,
    CheckIcon, XMarkIcon, ChevronDownIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const ENV_COLORS: Record<string, string> = {
    prod: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    production: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    staging: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    dev: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    local: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};
const envColor = (n: string) => ENV_COLORS[n.toLowerCase()] ?? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

const blankEnv = (): DeploymentEnvironment => ({
    name: '', keycloak: { baseUrl: '', adminUrl: '' },
    vault: { url: '', secretStoreName: '' },
    messageBus: { connectionString: '', daprComponent: '' },
    redis: { host: '', passwordSecretKey: '' },
    clusters: [], notes: '',
});

const EnvForm: React.FC<{
    initial: DeploymentEnvironment;
    onSave: (v: DeploymentEnvironment) => Promise<void>;
    onCancel: () => void;
    isNew?: boolean;
}> = ({ initial, onSave, onCancel, isNew }) => {
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    return (
        <Formik initialValues={initial} onSubmit={async (v) => {
            setSaving(true); setErr(null);
            try { await onSave(v); } catch (e: any) { setErr(e?.message ?? 'Save failed'); }
            finally { setSaving(false); }
        }}>
            {({ values }) => (
                <Form className="space-y-4 p-4 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {isNew && (
                            <div className="md:col-span-2">
                                <label className={labelCls}>Environment name *</label>
                                <Field name="name" placeholder="e.g. dev, staging, prod" className={inputCls} />
                            </div>
                        )}

                        <div className="md:col-span-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Keycloak</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Base URL</label>
                                    <Field name="keycloak.baseUrl" placeholder="https://keycloak.example.com" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Admin URL (if different)</label>
                                    <Field name="keycloak.adminUrl" placeholder="same as base URL" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Realm suffix</label>
                                    <Field name="keycloak.realmSuffix" placeholder="optional, appended to tenantId" className={inputCls} />
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Vault (per environment)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Vault URL</label>
                                    <Field name="vault.url" placeholder="https://my-vault.vault.azure.net" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Secret store name</label>
                                    <Field name="vault.secretStoreName" placeholder="azure-key-vault" className={inputCls} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Redis</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Host</label>
                                    <Field name="redis.host" placeholder="redis.example.com:6380" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Password secret key</label>
                                    <Field name="redis.passwordSecretKey" placeholder="redis-password" className={inputCls} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Message Bus</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Connection string / URL</label>
                                    <Field name="messageBus.connectionString" placeholder="amqp://..." className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Dapr component name</label>
                                    <Field name="messageBus.daprComponent" placeholder="pubsub" className={inputCls} />
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelCls}>Notes</label>
                            <Field as="textarea" name="notes" rows={2} className={`${inputCls} resize-none`} placeholder="Optional notes about this environment" />
                        </div>
                    </div>

                    {err && <p className="text-xs text-red-600 dark:text-red-400">{err}</p>}

                    <div className="flex gap-2">
                        <button type="submit" disabled={saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50">
                            <CheckIcon className="h-3.5 w-3.5" />
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={onCancel}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <XMarkIcon className="h-3.5 w-3.5" /> Cancel
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

const EnvironmentDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [dep, setDep]                   = useState<Deployment | null>(null);
    const [loading, setLoading]           = useState(true);
    const [editingDep, setEditingDep]     = useState(false);
    const [editingEnv, setEditingEnv]     = useState<string | null>(null);
    const [addingEnv, setAddingEnv]       = useState(false);
    const [savingDep, setSavingDep]       = useState(false);
    const [expandedEnvs, setExpandedEnvs] = useState<Set<string>>(new Set());

    const load = async () => {
        if (!id) return;
        try {
            const r = await getDeploymentById(id);
            setDep(r.data);
            // Expand all environments by default
            setExpandedEnvs(new Set(r.data.environments.map(e => e.name)));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const toggleEnv = (name: string) => {
        setExpandedEnvs(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const handleSaveDeployment = async (values: any) => {
        if (!id) return;
        setSavingDep(true);
        try {
            const r = await updateDeployment(id, values);
            setDep(r.data);
            setEditingDep(false);
        } finally { setSavingDep(false); }
    };

    const handleSaveEnv = async (envName: string, values: DeploymentEnvironment) => {
        if (!id) return;
        const r = await updateEnvironment(id, envName, values);
        setDep(r.data);
        setEditingEnv(null);
    };

    const handleAddEnv = async (values: DeploymentEnvironment) => {
        if (!id || !values.name.trim()) return;
        const r = await addEnvironment(id, values);
        setDep(r.data);
        setAddingEnv(false);
        setExpandedEnvs(prev => new Set([...prev, values.name]));
    };

    const handleDeleteEnv = async (envName: string) => {
        if (!id || !window.confirm(`Delete environment "${envName}"?`)) return;
        const r = await deleteEnvironment(id, envName);
        setDep(r.data);
    };

    if (loading) return (
        <div className="flex items-center gap-2 px-6 py-6 text-gray-400 text-sm">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-primary-500 rounded-full" /> Loading…
        </div>
    );
    if (!dep) return <div className="px-6 py-6 text-red-500">Deployment not found</div>;

    return (
        <div className="px-6 py-6 space-y-6 max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button onClick={() => navigate('/environments')}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{dep.name}</h1>
                    {dep.description && <p className="text-sm text-gray-500 dark:text-gray-400">{dep.description}</p>}
                </div>
                {!editingDep && (
                    <button onClick={() => setEditingDep(true)}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <PencilSquareIcon className="h-4 w-4" /> Edit deployment
                    </button>
                )}
            </div>

            {/* Deployment settings */}
            {editingDep ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Deployment settings</h2>
                    <Formik initialValues={dep} onSubmit={handleSaveDeployment}>
                        <Form className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Name *</label>
                                    <Field name="name" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Type</label>
                                    <Field as="select" name="type" className={inputCls}>
                                        <option value="azure">Azure</option>
                                        <option value="kubernetes">Kubernetes</option>
                                        <option value="docker">Docker</option>
                                        <option value="hybrid">Hybrid</option>
                                    </Field>
                                </div>
                                <div className="col-span-2">
                                    <label className={labelCls}>Description</label>
                                    <Field name="description" className={inputCls} />
                                </div>
                                <div className="col-span-2">
                                    <label className={labelCls}>Domain base</label>
                                    <Field name="domainBase" placeholder="e.g. ontoris.ch" className={inputCls} />
                                </div>
                            </div>

                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">ACR (Azure Container Registry)</p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelCls}>Login server</label>
                                    <Field name="acr.loginServer" placeholder="myacr.azurecr.io" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Resource group</label>
                                    <Field name="acr.resourceGroup" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Subscription ID</label>
                                    <Field name="acr.subscriptionId" className={inputCls} />
                                </div>
                            </div>

                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Vault (deployment-level)</p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelCls}>Vault URL</label>
                                    <Field name="vault.url" placeholder="https://my-vault.vault.azure.net" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Secret store name</label>
                                    <Field name="vault.secretStoreName" placeholder="azure-key-vault" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Azure tenant ID</label>
                                    <Field name="vault.tenantId" className={inputCls} />
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Field type="checkbox" name="isDefault" id="isDefault" className="rounded border-gray-300" />
                                <label htmlFor="isDefault" className="text-sm text-gray-600 dark:text-gray-300">Mark as default deployment</label>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button type="submit" disabled={savingDep}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50">
                                    <CheckIcon className="h-3.5 w-3.5" />{savingDep ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" onClick={() => setEditingDep(false)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <XMarkIcon className="h-3.5 w-3.5" /> Cancel
                                </button>
                            </div>
                        </Form>
                    </Formik>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><span className="text-xs text-gray-400 block">Type</span><span className="font-medium text-gray-700 dark:text-gray-300">{dep.type}</span></div>
                        <div><span className="text-xs text-gray-400 block">Domain</span><span className="font-medium text-gray-700 dark:text-gray-300">{dep.domainBase || '—'}</span></div>
                        <div><span className="text-xs text-gray-400 block">ACR</span><span className="font-medium text-gray-700 dark:text-gray-300 text-xs">{dep.acr?.loginServer || '—'}</span></div>
                        <div><span className="text-xs text-gray-400 block">Vault</span><span className="font-medium text-gray-700 dark:text-gray-300 text-xs">{dep.vault?.url?.replace(/^https?:\/\//, '') || '—'}</span></div>
                    </div>
                </div>
            )}

            {/* Environments */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Environments</h2>
                    <button onClick={() => { setAddingEnv(true); setEditingEnv(null); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                        <PlusIcon className="h-4 w-4" /> Add environment
                    </button>
                </div>

                {addingEnv && (
                    <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New environment</p>
                        <EnvForm initial={blankEnv()} onSave={handleAddEnv} onCancel={() => setAddingEnv(false)} isNew />
                    </div>
                )}

                <div className="space-y-2">
                    {dep.environments.length === 0 && !addingEnv && (
                        <p className="text-sm text-gray-400 dark:text-gray-500 py-2">No environments yet — add one above.</p>
                    )}
                    {dep.environments.map(env => (
                        <div key={env.name} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-4 py-3 flex items-center gap-3">
                                <button onClick={() => toggleEnv(env.name)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                    {expandedEnvs.has(env.name)
                                        ? <ChevronDownIcon className="h-4 w-4" />
                                        : <ChevronRightIcon className="h-4 w-4" />}
                                </button>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${envColor(env.name)}`}>{env.name}</span>
                                {env.keycloak?.baseUrl && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                        KC: {env.keycloak.baseUrl.replace(/^https?:\/\//, '')}
                                    </span>
                                )}
                                <div className="ml-auto flex items-center gap-1">
                                    <button onClick={() => { setEditingEnv(env.name); setAddingEnv(false); }}
                                        className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <PencilSquareIcon className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => handleDeleteEnv(env.name)}
                                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                                        <TrashIcon className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>

                            {expandedEnvs.has(env.name) && editingEnv === env.name && (
                                <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                                    <EnvForm
                                        initial={env}
                                        onSave={(v) => handleSaveEnv(env.name, v)}
                                        onCancel={() => setEditingEnv(null)}
                                    />
                                </div>
                            )}

                            {expandedEnvs.has(env.name) && editingEnv !== env.name && (
                                <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                    {env.keycloak?.baseUrl && (
                                        <div><span className="text-gray-400 block">Keycloak</span><span className="text-gray-700 dark:text-gray-300">{env.keycloak.baseUrl}</span></div>
                                    )}
                                    {env.vault?.url && (
                                        <div><span className="text-gray-400 block">Vault</span><span className="text-gray-700 dark:text-gray-300">{env.vault.url}</span></div>
                                    )}
                                    {env.vault?.secretStoreName && (
                                        <div><span className="text-gray-400 block">Secret store</span><span className="text-gray-700 dark:text-gray-300">{env.vault.secretStoreName}</span></div>
                                    )}
                                    {env.redis?.host && (
                                        <div><span className="text-gray-400 block">Redis</span><span className="text-gray-700 dark:text-gray-300">{env.redis.host}</span></div>
                                    )}
                                    {env.messageBus?.connectionString && (
                                        <div><span className="text-gray-400 block">Message bus</span><span className="text-gray-700 dark:text-gray-300 truncate block max-w-xs">{env.messageBus.connectionString}</span></div>
                                    )}
                                    {env.messageBus?.daprComponent && (
                                        <div><span className="text-gray-400 block">Dapr component</span><span className="text-gray-700 dark:text-gray-300">{env.messageBus.daprComponent}</span></div>
                                    )}
                                    {env.clusters && env.clusters.length > 0 && (
                                        <div><span className="text-gray-400 block">Clusters</span><span className="text-gray-700 dark:text-gray-300">{env.clusters.join(', ')}</span></div>
                                    )}
                                    {env.notes && (
                                        <div className="col-span-2 md:col-span-3"><span className="text-gray-400 block">Notes</span><span className="text-gray-700 dark:text-gray-300">{env.notes}</span></div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EnvironmentDetailsPage;
